import is from "@sindresorhus/is";

type TreeLikeData = Record<string, unknown> | Record<string, unknown>[] | (string | number)[];

type JSONSchemaTypeName = "string" | "number" | "integer" | "boolean" | "object" | "array" | "null" | "any";

type JSONSchemaType = string | number | boolean | JSONSchemaObject | JSONSchemaArray | null;

interface JSONSchemaObject {
  [key: string]: JSONSchemaType;
}

type JSONSchemaArray = Array<JSONSchemaType>;

export interface MappingsJSONSchemaDefinition {
  type?: JSONSchemaTypeName | JSONSchemaTypeName[] | undefined;
  description?: string | undefined;
  properties?: Record<string, MappingsJSONSchemaDefinition> | undefined;
  maxLength?: number | undefined;
  items?: MappingsJSONSchemaDefinition | MappingsJSONSchemaDefinition[] | undefined;
  enum?: (string | number | null)[] | undefined;
  title?: string | undefined;
  format?: string | undefined;
  required?: string[] | undefined;
  default?: JSONSchemaType | undefined;
  anyOf?: MappingsJSONSchemaDefinition[] | undefined;
  example?: string | number | boolean | JSONSchemaObject | undefined;
}

/**
 * To my best knowledge, the types for the 2020-12 draft were not created yet.
 * Since the spec is vast in terms of keywords, custom types were written to reduce the mental overhead while working with the generated schema.
 */
interface ObjectRootMappingsJSONSchema extends Omit<MappingsJSONSchemaDefinition, "default"> {
  $schema: "https://json-schema.org/draft/2020-12/schema";
  // Assumes that the JSONData is an object
  properties: Record<string, MappingsJSONSchemaDefinition>;
  default: Record<string, unknown>;
  required?: string[];
}

interface ArrayRootMappingsJSONSchema extends Omit<MappingsJSONSchemaDefinition, "default"> {
  $schema: "https://json-schema.org/draft/2020-12/schema";
  default: Record<string, unknown>[];
}

export type MappingsJSONSchema = ObjectRootMappingsJSONSchema | ArrayRootMappingsJSONSchema;

export const JSONToJSONSchema = (JSONData: TreeLikeData): MappingsJSONSchema => {
  const schemaBase: Pick<MappingsJSONSchema, "$schema"> = {
    $schema: "https://json-schema.org/draft/2020-12/schema",
  };

  /**
   * The result is split for readability and "TypeScript stop screaming at me!" purposes.
   */
  const schemaDefinitionFromData = schemaFromData(JSONData);

  return {
    ...schemaBase,
    ...schemaDefinitionFromData,
    default: JSONData as Record<string, unknown>[],
  };

  function schemaFromData(data: unknown): MappingsJSONSchemaDefinition {
    if (is.plainObject(data)) {
      const objectSchemaBase = {
        type: "object",
        properties: {} as Record<string, MappingsJSONSchemaDefinition>,
      } as const;

      const keys = Object.keys(data);
      keys.forEach((key) => {
        objectSchemaBase.properties[key] = schemaFromData(data[key]);
      });

      return objectSchemaBase;
    }

    if (is.array(data)) {
      const firstArrayItem = data[0];
      return {
        type: "array",
        items: schemaFromData(firstArrayItem),
      };
    }

    if (is.boolean(data)) {
      return { type: "boolean" };
    }

    if (is.number(data)) {
      return { type: "number" };
    }

    if (is.string(data)) {
      return {
        type: "string",
      };
    }

    return { type: "null" };
  }
};
